import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Zap, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { TriggerField, isFieldVisible } from './TriggerField'
import { isVariableGroupSelectionBlocked, selectableGroupIds, VariableGroupsField } from '@/components/projects/workflows/shared'
import { dedupeRepositoryContextSources } from '@/components/projects/workflows/shared/contextSourceDedup'
import { useContextSources, useVariableGroupTriggerOptions } from '@/hooks'
import type { TriggerInputField, WorkflowTriggerSchema } from '@/types/workflowDefinition'
import type { ContextSourceListItem } from '@/types'

type FormValues = Record<string, string>

interface WorkflowTriggerFormProps {
  projectId: string
  workflowName: string
  workflowDisplayName: string
  version: string
  description: string | null
  schema: WorkflowTriggerSchema
  isTriggering: boolean
  onTrigger: (inputValuesJson: string, variableGroupIds: string[]) => void
  onCancel: () => void
}

function buildDefaults(fields: TriggerInputField[], contextItems: ContextSourceListItem[] = []): FormValues {
  const defaults: FormValues = {}
  for (const field of fields) {
    if (field.type === 'boolean') {
      defaults[field.name] = field.default ?? 'false'
    } else if (field.type === 'context-picker' && field.multiSelect && contextItems.length > 0) {
      const filtered = field.contextType
        ? contextItems.filter(s => s.kind.toLowerCase() === field.contextType!.toLowerCase())
        : contextItems

      const deduped = dedupeRepositoryContextSources(filtered)

      defaults[field.name] = deduped.length > 0 ? JSON.stringify(deduped.map(s => s.id)) : (field.default ?? '')
    } else {
      defaults[field.name] = field.default ?? ''
    }
  }
  return defaults
}

export function WorkflowTriggerForm({
  projectId,
  workflowName,
  workflowDisplayName,
  version,
  description,
  schema,
  isTriggering,
  onTrigger,
  onCancel,
}: Readonly<WorkflowTriggerFormProps>) {
  const fields = schema.fields
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onBlur' })

  const hasContextPickerMultiSelect = useMemo(
    () => fields.some(f => f.type === 'context-picker' && f.multiSelect),
    [fields],
  )
  const { data: contextData, isLoading: contextSourcesLoading } = useContextSources(
    projectId,
    { maxResultCount: 1000 },
    undefined,
    { enabled: hasContextPickerMultiSelect },
  )
  const contextItems: ContextSourceListItem[] = useMemo(
    () => contextData?.items ?? [],
    [contextData],
  )

  const defaultsSessionRef = useRef<string | null>(null)
  const [variableGroupIds, setVariableGroupIds] = useState<string[]>([])
  const { data: variableGroupOptions, isLoading: variableGroupsLoading, error: variableGroupsError } = useVariableGroupTriggerOptions(projectId, { workflowName })

  useEffect(() => {
    if (hasContextPickerMultiSelect && contextSourcesLoading) return

    const fieldsKey = fields.map(f => f.name).join(',')
    if (defaultsSessionRef.current === fieldsKey) return

    defaultsSessionRef.current = fieldsKey
    reset(buildDefaults(fields, contextItems))
  }, [fields, contextItems, contextSourcesLoading, hasContextPickerMultiSelect, reset])

  const onSubmit = (data: FormValues) => {
    const visibleFieldNames = new Set(
      fields.filter((f) => isFieldVisible(f, data as Record<string, unknown>)).map((f) => f.name),
    )
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([key]) => visibleFieldNames.has(key)),
    )
    onTrigger(JSON.stringify(filteredData), selectableGroupIds(variableGroupOptions, variableGroupIds))
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="border rounded-lg bg-card" data-testid="workflow-trigger-form">
      <div className="sticky top-0 z-10 px-3 py-2 border-b bg-background flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{workflowDisplayName}</span>
            <Badge variant="secondary" className="text-[10px]">v{version}</Badge>
          </div>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          )}
        </div>
        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onCancel} aria-label="Close form">
          <X className="h-3 w-3" />
        </Button>
      </div>

      {fields.length > 0 && (
        <div className="p-3 space-y-3 max-h-[50vh] overflow-y-auto overflow-x-hidden">
          {fields.map((field) => (
            <TriggerField
              key={field.name}
              projectId={projectId}
              field={field}
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
            />
          ))}
        </div>
      )}

      <div className="p-3 border-t">
        <VariableGroupsField
          projectId={projectId}
          options={variableGroupOptions}
          isLoading={variableGroupsLoading}
          error={variableGroupsError}
          selectedIds={variableGroupIds}
          onSelectedIdsChange={setVariableGroupIds}
        />
      </div>

      <div className="px-3 py-2 border-t flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={isTriggering || isVariableGroupSelectionBlocked(variableGroupOptions, variableGroupsLoading, variableGroupsError)}
        >
          {isTriggering ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Triggering...
            </>
          ) : (
            <>
              <Zap className="h-3 w-3 mr-1" />
              Trigger
            </>
          )}
        </Button>
      </div>
    </form>
  )
}
